// pattern: Imperative Shell
// Typed fetch wrappers for /api/ endpoints

import type { StoredQuiz, DashboardData, ExtractionResult } from '../core/question-model.ts';
import type { FlashcardExtractionResult } from '../core/flashcard-model.ts';

export type ApiResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly error: string };

const API_BASE = '/api';

async function apiGet<T>(path: string): Promise<ApiResult<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: body.error || `HTTP ${response.status}`,
      };
    }
    const data = await response.json();
    return { ok: true, data: data as T };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'network error',
    };
  }
}

async function apiPost<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const resBody = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: resBody.error || `HTTP ${response.status}`,
      };
    }
    const data = await response.json();
    return { ok: true, data: data as T };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'network error',
    };
  }
}

async function apiPut<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const resBody = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: resBody.error || `HTTP ${response.status}`,
      };
    }
    const data = await response.json();
    return { ok: true, data: data as T };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'network error',
    };
  }
}

// --- Public API ---

export function createDashboard(name?: string): Promise<ApiResult<{ readonly id: string }>> {
  return apiPost('/dashboard', name ? { name } : undefined);
}

export function getDashboard(id: string): Promise<ApiResult<DashboardData>> {
  return apiGet(`/dashboard?id=${encodeURIComponent(id)}`);
}

export function createQuiz(quiz: {
  readonly dashboardId: string;
  readonly name: string;
  readonly description?: string;
  readonly questions: ReadonlyArray<unknown>;
  readonly flashcards?: ReadonlyArray<unknown>;
  readonly mode: 'structured' | 'pool' | 'flashcard';
  readonly poolSize?: number;
}): Promise<ApiResult<{ readonly id: string }>> {
  return apiPost('/quiz', quiz);
}

export function getQuiz(id: string): Promise<ApiResult<StoredQuiz>> {
  return apiGet(`/quiz?id=${encodeURIComponent(id)}`);
}

export function updateQuiz(
  id: string,
  quiz: { readonly dashboardId: string } & Record<string, unknown>
): Promise<ApiResult<{ readonly ok: boolean }>> {
  return apiPut(`/quiz?id=${encodeURIComponent(id)}`, quiz);
}

export async function extractQuestions(file: File): Promise<ApiResult<ExtractionResult>> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/extract`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: body.error || `HTTP ${response.status}`,
      };
    }

    const data = await response.json();
    return { ok: true, data: data as ExtractionResult };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'network error',
    };
  }
}

export async function extractGeneratedQuestions(file: File): Promise<ApiResult<ExtractionResult>> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', 'generate');

    const response = await fetch(`${API_BASE}/extract`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: body.error || `HTTP ${response.status}`,
      };
    }

    const data = await response.json();
    return { ok: true, data: data as ExtractionResult };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'network error',
    };
  }
}

export async function extractFlashcards(file: File): Promise<ApiResult<FlashcardExtractionResult>> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', 'flashcard');

    const response = await fetch(`${API_BASE}/extract`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: body.error || `HTTP ${response.status}`,
      };
    }

    const data = await response.json();
    return { ok: true, data: data as FlashcardExtractionResult };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'network error',
    };
  }
}
